/**
 * Trust Center — per-client 90-day SLA + incident timeline + downloadable PDF.
 * Same password gate as the public status page (uses the same token).
 * Route: /trust/:token
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Logo from "@/components/Logo";
import {
  ShieldCheck, ShieldStar, ArrowsClockwise, Timer, FilePdf, Warning,
  CheckCircle, HardDrives, Pulse, ArrowLeft, Hourglass,
} from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL + "/api";

function fmtDur(seconds) {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

export default function TrustCenter() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [password, setPassword] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [error, setError] = useState("");

  const loadMeta = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await axios.get(`${BASE}/public-status/${token}/meta`);
      setMeta(data);
      if (!data.has_password) await loadData("");
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  const loadData = async (pw) => {
    setSubmitting(true); setError("");
    try {
      const { data } = await axios.post(
        `${BASE}/public-status/${token}/trust`,
        { password: pw || null },
      );
      setData(data);
    } catch (e) {
      setData(null);
      setError(e.response?.data?.detail || e.message);
    } finally { setSubmitting(false); }
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const resp = await axios.post(
        `${BASE}/public-status/${token}/trust.pdf`,
        { password: password || null },
        { responseType: "blob" },
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Trust-Report-${(data?.client_name || "client").replace(/\s+/g, "_")}-90d.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setPdfBusy(false); }
  };

  useEffect(() => { loadMeta(); }, [token]);

  if (loading) {
    return <FullCenter><div className="text-xs text-slate-400">loading…</div></FullCenter>;
  }
  if (!meta) {
    return (
      <FullCenter>
        <div className="text-center">
          <h1 className="font-display text-2xl font-bold text-white mb-2">Trust report not found</h1>
          <p className="text-sm text-slate-400">This URL is invalid, expired, or has been disabled.</p>
        </div>
      </FullCenter>
    );
  }

  // Password gate (same as public status)
  if (meta.has_password && !data) {
    return (
      <FullCenter>
        <div className="sw-card p-7 w-full max-w-sm" data-testid="trust-password-card">
          <div className="mb-6"><Logo size={40} /></div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-brand-cyan mb-1">Trust Center · 90 days</div>
          <h1 className="font-display text-xl font-bold tracking-tight text-white mb-1">{meta.name}</h1>
          <p className="text-xs text-slate-400 mb-5 inline-flex items-center gap-1.5">
            <ShieldCheck size={14} weight="duotone" /> Password protected
          </p>
          <form onSubmit={(e) => { e.preventDefault(); loadData(password); }} className="space-y-3" data-testid="trust-password-form">
            <input type="password" placeholder="Password" value={password}
              onChange={(e) => setPassword(e.target.value)} required
              data-testid="trust-password-input"
              className="w-full bg-background-deep border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-cyan" />
            {error && <div className="text-xs text-red-400" data-testid="trust-password-error">{error}</div>}
            <button type="submit" disabled={submitting} data-testid="trust-unlock-btn"
              className="w-full bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 disabled:opacity-60 rounded-md px-4 py-2 text-sm font-semibold">
              {submitting ? "Unlocking…" : "Unlock Trust Report"}
            </button>
            <Link to={`/status/${token}`}
              data-testid="trust-back-to-status"
              className="block text-center text-xs text-slate-400 hover:text-brand-cyan pt-2">
              ← Live status page
            </Link>
          </form>
        </div>
      </FullCenter>
    );
  }

  if (error) return <FullCenter><div className="text-sm text-red-400">{error}</div></FullCenter>;
  if (!data) return <FullCenter><div className="text-xs text-slate-400">loading data…</div></FullCenter>;

  const sla = data.sla;
  const slaColor = sla.met ? "text-emerald-400" : "text-red-400";

  return (
    <div className="min-h-screen bg-background-deep text-white sw-grid-bg" data-testid="trust-page">
      <header className="border-b border-border bg-brand-navy/95 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto h-20 px-6 flex items-center justify-between">
          <Logo size={48} />
          <div className="flex items-center gap-3">
            <Link to={`/status/${token}`} data-testid="trust-live-status-link"
              className="text-xs inline-flex items-center gap-1.5 text-slate-300 hover:text-brand-cyan">
              <ArrowLeft size={12} weight="bold" /> Live status
            </Link>
            <button onClick={() => loadData(password)} disabled={submitting}
              data-testid="trust-refresh-btn"
              className="text-xs inline-flex items-center gap-1.5 text-slate-300 hover:text-brand-cyan">
              <ArrowsClockwise size={12} weight="bold" /> Refresh
            </button>
            <button onClick={downloadPdf} disabled={pdfBusy}
              data-testid="trust-pdf-btn"
              className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 border border-brand-cyan/40 bg-brand-cyan/10 hover:bg-brand-cyan/20 text-brand-cyan rounded">
              <FilePdf size={12} weight="bold" /> {pdfBusy ? "Building…" : "Download 90-day PDF"}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-8">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-brand-cyan mb-1 flex items-center gap-1.5">
            <ShieldStar size={11} weight="duotone" /> TRUST CENTER · 90 DAYS
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white mb-2"
            data-testid="trust-client-name">
            {data.client_name}
          </h1>
          <p className="text-xs text-slate-500">
            window {new Date(data.period.since).toLocaleDateString()} → {new Date(data.period.until).toLocaleDateString()}
            {" · "}generated {new Date(data.generated_at).toLocaleString()}
          </p>
        </div>

        {/* Big SLA banner */}
        <section className={`sw-card p-8 ${sla.met ? "border-emerald-500/40 bg-emerald-500/5" : "border-red-500/40 bg-red-500/5"}`}
          data-testid="trust-sla-banner">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1">90-DAY UPTIME</div>
              <div className={`font-display text-5xl sm:text-6xl font-bold ${slaColor}`}
                data-testid="trust-sla-pct">
                {sla.overall_uptime_pct != null ? `${sla.overall_uptime_pct.toFixed(2)}%` : "—"}
              </div>
              <div className="text-xs text-slate-400 mt-2">
                Target {sla.target_pct.toFixed(2)}% · <span className={slaColor}>{sla.met ? "MET" : "MISSED"}</span>
              </div>
            </div>
            <Metric label="MTTR" value={fmtDur(sla.mttr_seconds)} icon={Timer} accent="text-brand-cyan" testid="trust-mttr" />
            <Metric label="Incidents (90d)"
              value={`${sla.incidents_closed} closed · ${sla.incidents_open} open`}
              icon={sla.incidents_open ? Warning : CheckCircle}
              accent={sla.incidents_open ? "text-red-400" : "text-emerald-400"}
              testid="trust-incidents-count" />
          </div>
        </section>

        {/* Daily uptime sparkline */}
        {data.daily_uptime.length > 0 && (
          <section className="sw-card p-6" data-testid="trust-uptime-chart">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-display text-base font-bold tracking-tight">Daily uptime · last {data.period.days} days</h2>
                <p className="text-[11px] text-slate-500">
                  Each bar = one day. Hover to inspect.
                </p>
              </div>
              <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.15em]">
                <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 bg-emerald-400" /> &ge;99%</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 bg-amber-400" /> 95–99%</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 bg-red-400" /> &lt;95%</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 bg-slate-700" /> no data</span>
              </div>
            </div>
            <UptimeChart series={data.daily_uptime} />
          </section>
        )}

        {/* Monitors + Backups summary */}
        {(data.monitors.length > 0 || data.backups.length > 0) && (
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {data.monitors.length > 0 && (
              <SummaryTable
                title="Uptime monitors"
                rows={data.monitors.map(m => ({
                  name: m.name,
                  meta: `${m.type?.toUpperCase()} · ${m.target}`,
                  metric: m.uptime_pct != null ? `${m.uptime_pct.toFixed(2)}%` : "—",
                  metricColor: (m.uptime_pct ?? 0) >= 99 ? "text-emerald-400" : (m.uptime_pct ?? 0) >= 95 ? "text-amber-400" : "text-red-400",
                  icon: Pulse,
                }))}
                testid="trust-monitors-table"
              />
            )}
            {data.backups.length > 0 && (
              <SummaryTable
                title="Backup jobs"
                rows={data.backups.map(b => ({
                  name: b.name,
                  meta: `${(b.vendor || "—").toUpperCase()} · ${b.runs} runs`,
                  metric: b.success_pct != null ? `${b.success_pct.toFixed(2)}%` : "—",
                  metricColor: (b.success_pct ?? 0) >= 99 ? "text-emerald-400" : (b.success_pct ?? 0) >= 95 ? "text-amber-400" : "text-red-400",
                  icon: HardDrives,
                }))}
                testid="trust-backups-table"
              />
            )}
          </section>
        )}

        {/* Incident timeline */}
        <section className="sw-card p-6" data-testid="trust-incidents-timeline">
          <h2 className="font-display text-base font-bold tracking-tight mb-1">Incident timeline · last {data.period.days} days</h2>
          <p className="text-[11px] text-slate-500 mb-4">
            All incidents — open, ack&apos;d, and closed.
          </p>
          {data.incidents.length === 0 ? (
            <div className="text-sm text-slate-400 inline-flex items-center gap-2 py-3">
              <CheckCircle size={14} weight="fill" className="text-emerald-400" />
              No incidents recorded in this window. Clean record.
            </div>
          ) : (
            <ul className="space-y-2">
              {data.incidents.map(inc => (
                <li key={inc.id} className="flex items-start gap-3 py-2 border-t border-border first:border-t-0"
                  data-testid={`trust-incident-${inc.id}`}>
                  <div className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${
                    inc.status === "closed" ? "bg-emerald-400"
                    : inc.status === "ack" ? "bg-amber-400" : "bg-red-400"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">
                      {inc.subject_name}
                      <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500 ml-2">
                        {inc.subject_type === "monitor" ? "monitor" : "backup"}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-400">
                      {new Date(inc.opened_at).toLocaleString()}
                      {inc.duration_seconds != null
                        ? <> · <Hourglass size={9} weight="bold" className="inline" /> {fmtDur(inc.duration_seconds)}</>
                        : <> · <span className="text-red-400">ongoing</span></>}
                    </div>
                  </div>
                  <StatusPill status={inc.status} severity={inc.severity} />
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>

      <footer className="border-t border-border py-6 text-center text-xs text-slate-500">
        Powered by <span className="text-brand-cyan">SentryWatch</span>
        {" · "}<span className="text-slate-600">password-protected</span>
      </footer>
    </div>
  );
}

function UptimeChart({ series }) {
  const colorFor = (pct) => {
    if (pct == null) return "bg-slate-700";
    if (pct >= 99) return "bg-emerald-400";
    if (pct >= 95) return "bg-amber-400";
    return "bg-red-400";
  };
  const max = Math.max(...series.map(s => s.checks || 0), 1);
  return (
    <div className="flex items-end gap-0.5 h-32" data-testid="trust-uptime-bars">
      {series.map(s => {
        const heightPct = s.uptime_pct == null ? 8 : Math.max(8, s.uptime_pct);
        return (
          <div key={s.date}
            title={`${s.date} · ${s.uptime_pct != null ? s.uptime_pct.toFixed(2) + "%" : "no data"} · ${s.checks} checks`}
            data-testid={`uptime-bar-${s.date}`}
            className={`flex-1 ${colorFor(s.uptime_pct)} rounded-t transition-opacity hover:opacity-75`}
            style={{ height: `${heightPct}%`, opacity: s.checks ? 0.7 + 0.3 * (s.checks / max) : 0.3 }} />
        );
      })}
    </div>
  );
}

function Metric({ label, value, icon: Icon, accent, testid }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1 flex items-center gap-1.5">
        {Icon && <Icon size={11} weight="duotone" />} {label}
      </div>
      <div className={`font-display text-3xl font-bold ${accent || "text-white"}`} data-testid={testid}>{value}</div>
    </div>
  );
}

function SummaryTable({ title, rows, testid }) {
  return (
    <div className="sw-card p-4" data-testid={testid}>
      <h3 className="font-display text-sm font-bold tracking-tight mb-3">{title}</h3>
      <ul className="space-y-1.5">
        {rows.map((r) => (
          <li key={`${title}::${r.name}`} className="flex items-center gap-2 text-sm">
            <r.icon size={13} weight="duotone" className="text-brand-cyan shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-white truncate">{r.name}</div>
              <div className="text-[10px] text-slate-500 truncate">{r.meta}</div>
            </div>
            <div className={`text-xs ${r.metricColor}`}>{r.metric}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatusPill({ status, severity }) {
  const tone = status === "closed" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
    : status === "ack" ? "bg-amber-500/15 text-amber-300 border-amber-500/40"
    : "bg-red-500/15 text-red-300 border-red-500/40";
  const label = status === "open" ? (severity || "open").toUpperCase() : status.toUpperCase();
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] border rounded ${tone} shrink-0`}>
      {label}
    </span>
  );
}

function FullCenter({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background-deep text-white sw-grid-bg p-6">
      {children}
    </div>
  );
}
